import { sql } from 'drizzle-orm';
import {
  pgTable,
  pgPolicy,
  index,
  uniqueIndex,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  bigint,
  doublePrecision,
  date,
  uuid,
} from "drizzle-orm/pg-core";
import type { StormCheckResult } from '../stormCheck';
import { claims } from './claims';
import { organizations } from './organizations';
import { users } from './users';

// Storm check (MIS-195, NOAA proof of concept). Contract: shared/stormCheck.ts.

// A claim's last storm check: the geocoded property address and the result. One row per claim, replaced on each run.
export const claimStormChecks = pgTable("claim_storm_checks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").notNull().references(() => claims.id, { onDelete: "cascade" }),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),
  // The address as sent to the geocoder; a later run geocodes again only when it changes.
  geocodedAddress: text("geocoded_address").notNull(),
  latitude: doublePrecision("latitude").notNull(),
  longitude: doublePrecision("longitude").notNull(),
  geocodeMatch: varchar("geocode_match").notNull(), // 'exact' | 'approximate'
  dateOfLoss: date("date_of_loss").notNull(),
  radiusMiles: integer("radius_miles").notNull(),
  windowDays: integer("window_days").notNull(),
  confidence: varchar("confidence").notNull(), // 'high' | 'medium' | 'low' | 'none'
  result: jsonb("result").$type<StormCheckResult>().notNull(),
  ranBy: varchar("ran_by").references(() => users.id, { onDelete: "set null" }),
  ranAt: timestamp("ran_at").notNull().defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("claim_storm_checks_claim_id_idx").on(table.claimId),
  index("idx_claim_storm_checks_org").on(table.orgId),
  pgPolicy('claim_storm_checks_tenant_isolation', {
    as: 'permissive',
    for: 'all',
    to: 'app_user',
    using: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
    withCheck: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
  }),
]).enableRLS();

// Public NOAA and Census answers, cached so a day or an area is fetched once. Not firm data: no org_id.
export const stormSourceCache = pgTable("storm_source_cache", {
  // e.g. "noaa|spc|hail|240528" or "noaa|swdi-nx3hail|20240525:20240602|-97.00,32.60,-96.60,33.00"
  key: text("key").primaryKey(),
  source: varchar("source").notNull(), // 'spc' | 'swdi'
  httpStatus: integer("http_status").notNull(),
  body: text("body").notNull(),
  fetchedAt: timestamp("fetched_at").notNull().defaultNow(),
  // Null: never refetched (an older day NOAA no longer changes).
  expiresAt: timestamp("expires_at"),
});

// NCEI Storm Events Database rows (hail, thunderstorm wind, tornado), imported from the yearly
// details CSV by server/scripts/importStormEvents.ts. Public NOAA data: no org_id.
export const stormEvents = pgTable("storm_events", {
  eventId: bigint("event_id", { mode: "number" }).primaryKey(),
  episodeId: bigint("episode_id", { mode: "number" }),
  eventType: varchar("event_type").notNull(), // 'Hail' | 'Thunderstorm Wind' | 'Tornado'
  state: varchar("state"),
  stateFips: integer("state_fips"),
  czType: varchar("cz_type"), // 'C' county, 'Z' zone, 'M' marine
  czFips: integer("cz_fips"),
  czName: varchar("cz_name"),
  wfo: varchar("wfo"),
  czTimezone: varchar("cz_timezone"),
  beginAt: timestamp("begin_at", { withTimezone: true }).notNull(),
  endAt: timestamp("end_at", { withTimezone: true }),
  magnitude: doublePrecision("magnitude"), // hail inches, wind knots
  magnitudeType: varchar("magnitude_type"),
  torFScale: varchar("tor_f_scale"),
  damageProperty: varchar("damage_property"),
  damageCrops: varchar("damage_crops"),
  source: varchar("source"),
  beginLocation: varchar("begin_location"),
  beginLat: doublePrecision("begin_lat"),
  beginLon: doublePrecision("begin_lon"),
  endLat: doublePrecision("end_lat"),
  endLon: doublePrecision("end_lon"),
  episodeNarrative: text("episode_narrative"),
  eventNarrative: text("event_narrative"),
  dataSource: varchar("data_source"),
  sourceFile: varchar("source_file").notNull(),
  importedAt: timestamp("imported_at").notNull().defaultNow(),
}, (table) => [
  index("idx_storm_events_begin_at").on(table.beginAt),
  index("idx_storm_events_begin_lat_lon").on(table.beginLat, table.beginLon),
  index("idx_storm_events_county").on(table.stateFips, table.czType, table.czFips),
]);

export type ClaimStormCheck = typeof claimStormChecks.$inferSelect;
export type InsertClaimStormCheck = typeof claimStormChecks.$inferInsert;
export type StormSourceCacheRow = typeof stormSourceCache.$inferSelect;
export type StormEventRow = typeof stormEvents.$inferSelect;
export type InsertStormEventRow = typeof stormEvents.$inferInsert;
